import Layout from "../../components/Layout";
import { CampaignSummarySkeleton } from "@/components/CampaignSummary";
import { ContentTagsCellSkeleton, CreatorCellSkeleton, PitchStatusBadgeSkeleton, PriceOrStatusCellSkeleton, RowActionsSkeleton, TableShell, type TableColumn } from "@/components/tableKit";
import { Heading } from "@/components/typography";
import { Card, CardContent } from "@/components/ui/card";
import { useSkeletonPhases } from "@/hooks/useSkeletonPhases";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CampaignDetailsHeader } from "./components/CampaignDetailsHeader";
import { CampaignTabs } from "./components/CampaignTabs";
import { useCampaignDetails } from "./useCampaignDetails";

const CAMPAIGN_DETAILS_LOADING_COLUMNS: TableColumn<unknown>[] = [
    { id: "creator", header: "Creator", cell: () => null, skeleton: <CreatorCellSkeleton /> },
    { id: "price", header: "Price", cell: () => null, skeleton: <PriceOrStatusCellSkeleton /> },
    { id: "content", header: "Content", cell: () => null, skeleton: <ContentTagsCellSkeleton /> },
    { id: "status", header: "Status", cell: () => null, skeleton: <PitchStatusBadgeSkeleton /> },
    { id: "actions", header: "Actions", cell: () => null, sticky: true, skeleton: <RowActionsSkeleton /> },
];

export default function CampaignDetailsPage() {
    const {
        loading, campaign, campaignError, pitches, pitchesLoading, bookings, bookingsLoading, booker, client,
    } = useCampaignDetails();
    const phase = useSkeletonPhases(loading);

    if (phase === "showSkeleton") {
        return (
            <Layout>
                <div className="p-4">
                    <CampaignSummarySkeleton />
                    <Tabs value="pitches" className="gap-3">
                        <TabsList>
                            <TabsTrigger value="pitches">Pitches</TabsTrigger>
                            <TabsTrigger value="bookings">Bookings</TabsTrigger>
                        </TabsList>
                        <TabsContent value="pitches">
                            <Card>
                                <CardContent>
                                    <TableShell
                                        columns={CAMPAIGN_DETAILS_LOADING_COLUMNS}
                                        rows={[]}
                                        getRowId={() => ""}
                                        isLoading
                                    />
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>
            </Layout>
        );
    }

    if (phase === "showNothing") {
        return (
            <Layout>
                <div className="p-4" />
            </Layout>
        );
    }

    if (!campaign) {
        const notFound = !campaignError || campaignError.message.startsWith("Campaign not found");
        return (
            <Layout>
                <div className="my-12 text-center">
                    <Heading type="h3" className="mb-2">
                        {notFound ? "Campaign not found" : "Couldn’t load this campaign"}
                    </Heading>
                    <p className="text-muted-foreground">
                        {notFound ? "This campaign may have been deleted." : "Refresh and try again."}
                    </p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-4">
                <CampaignDetailsHeader campaign={campaign} client={client} pitches={pitches} />
                <CampaignTabs
                    campaign={campaign}
                    booker={booker}
                    pitches={pitches}
                    pitchesLoading={pitchesLoading}
                    bookings={bookings}
                    bookingsLoading={bookingsLoading}
                />
            </div>
        </Layout>
    );
}
